import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Container, Row, Col, Card, Button, Stack, Form, FormControl, Image,
    ToggleButtonGroup, ToggleButton,
    CardBody
} from 'react-bootstrap';
import { Paperclip, Building, Trash } from 'react-bootstrap-icons';
import authService from '../api-authorization/AuthorizeService';
import { useToast } from './Toast/ToastProvider';

/**
 * Generic Attachments component with inline bulk upload form
 *
 * Props:
 * - entityType: string                // "equipment" | "asset" | "capitalImprovement" | "capitalPlan" | ...
 * - entityId: number|string           // id of target entity
 * - foreignKeyField: string           // payload key for FK: e.g. "facilityEquipmentID" | "assetId" | "capitalPlanID"
 * - hideAddButton?: boolean
 * - isCustomerView?: boolean          // if true, disables add/edit/delete (read-only)
 * - initialAttachments?: array        // optional preloaded attachments (each item may have fileURL or base64)
 * - defaultPageSize?: number          // default 50
 * - pageSizeOptions?: number[]        // default [10, 20, 30, 50]
 * - additionalFields?: object         // additional fields to include in create/update payloads
 *
 * Optional advanced overrides (if your endpoints differ):
 * - endpoints?: {
 *     list: (entityType, entityId) => string,
 *     create: (entityType) => string,
 *     update: (entityType, id) => string,
 *     remove: (entityType, id) => string,
 *     types: () => string,
 *   }
 */
export default function Attachments({
    entityType,
    entityId,
    foreignKeyField,
    hideAddButton = false,
    isCustomerView = false,
    initialAttachments = null,
    defaultPageSize = 50,
    pageSizeOptions = [10, 20, 30, 50],
    endpoints,
    additionalFields = {},
    buttonStyle = {} // Optional: { backgroundColor: '#...', color: '#...' }
}) {
    const { showToast } = useToast();

    const api = useMemo(() => ({
        list: (t, id) => `api/${t}Attachment/${id}`,
        create: (t) => `api/${t}Attachment`,
        update: (t, id) => `api/${t}Attachment/${id}`,
        remove: (t, id) => `api/${t}Attachment/${id}`,
        types: () => `api/attachmenttypes/`,
        ...(endpoints || {})
    }), [endpoints]);

    // Data state
    const [attachments, setAttachments] = useState([]);
    const [attachmentTypes, setAttachmentTypes] = useState([]);
    const [selectedTypeIds, setSelectedTypeIds] = useState([]);
    // UI state
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [pageNumber, setPageNumber] = useState(1);
    const [modifyingAttachment, setModifyingAttachment] = useState(false);
    const [viewingAttachment, setViewingAttachment] = useState(false);
    const [isEditingAttachment, setIsEditingAttachment] = useState(false);
    const [validated, setValidated] = useState(false);
    const [currentAttachment, setCurrentAttachment] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileDescriptions, setFileDescriptions] = useState({});
    const [fileTypes, setFileTypes] = useState({});
    const [editingNewFile, setEditingNewFile] = useState(null);

    const isImage = (urlOrDataUrl) => {
        if (!urlOrDataUrl) return false;
        const s = urlOrDataUrl.toLowerCase();
        return s.startsWith('data:image')
            || s.includes('.jpg?') || s.endsWith('.jpg') || s.includes('.jpeg?') || s.endsWith('.jpeg')
            || s.includes('.png?') || s.endsWith('.png') || s.includes('.bmp?') || s.endsWith('.bmp')
            || s.includes('.gif?') || s.endsWith('.gif') || s.includes('.webp?') || s.endsWith('.webp');
    };

    const normalizeDataUrl = (a) => {
        // Normalize field names to camelCase for consistency
        const normalized = {
            ...a,
            attachmentTypeID: a.attachmentTypeID ?? a.AttachmentTypeID
        };
        // set data_url for rendering regardless if it came as fileURL, dataUri, or base64 pair
        if (a.fileURL) normalized.data_url = a.fileURL;
        else if (a.dataUri) normalized.data_url = a.dataUri;
        else if (a.data && a.dataType) normalized.data_url = a.dataType + 'base64,' + a.data;
        else if (!normalized.data_url) normalized.data_url = null;
        
        return normalized;
    };

    const fetchAttachments = useCallback(async () => {
        const token = await authService.getAccessToken();
        try {
            const res = await fetch(api.list(entityType, entityId), {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }
            const data = await res.json();
            if (Array.isArray(data)) {
                const normalized = data.map(normalizeDataUrl);
                setAttachments(normalized);
            } else {
                setAttachments([]);
            }
        } catch (error) {
            console.error('Error fetching attachments:', error);
            setAttachments([]);
        }
    }, [api, entityType, entityId]);

    const fetchTypes = useCallback(async () => {
        const token = await authService.getAccessToken();
        try {
            const res = await fetch(api.types(), {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }
            const data = await res.json();
            if (Array.isArray(data)) {
                setAttachmentTypes(data);
                setSelectedTypeIds(data.map(t => t.id)); // default: all selected
            } else {
                setAttachmentTypes([]);
                setSelectedTypeIds([]);
            }
        } catch (error) {
            console.error('Error fetching attachment types:', error);
            setAttachmentTypes([]);
            setSelectedTypeIds([]);
        }
    }, [api]);

    // Initial hydration
    useEffect(() => {
        (async () => {
            await fetchTypes();
            if (initialAttachments && Array.isArray(initialAttachments)) {
                setAttachments(initialAttachments.map(normalizeDataUrl));
            } else {
                await fetchAttachments();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityType, entityId]);

    // Derived for pagination
    const filtered = useMemo(() => {
        if (!selectedTypeIds?.length) return [];
        return attachments.filter(a => selectedTypeIds.includes(a.attachmentTypeID));
    }, [attachments, selectedTypeIds]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(pageNumber, totalPages);
    const pageSlice = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, pageSize, safePage]);

    const pageNumbers = useMemo(() => {
        if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
        // sliding window around current page (3 buttons)
        let min = safePage === 1 ? 1 : safePage - 1;
        let max = Math.min(totalPages, min + 2);
        if (max === safePage && max > 2) min = max - 2;
        return [min, min + 1, max].filter(n => n >= 1 && n <= totalPages && Number.isFinite(n));
    }, [safePage, totalPages]);

    // Form handlers
    const handleAddAttachment = () => {
        setCurrentAttachment({
            [foreignKeyField]: isNaN(entityId) ? entityId : Number(entityId),
            data: '',
            dataType: '',
            description: '',
            name: '',
            attachmentTypeID: attachmentTypes[0]?.id || '',
            data_url: ''
        });
        setSelectedFiles([]);
        setModifyingAttachment(true);
        setViewingAttachment(false);
        setValidated(false);
    };

    const handleViewAttachment = (att) => {
        // Ensure we have the complete attachment data
        const fullAttachment = {
            ...att,
            attachmentTypeID: att.attachmentTypeID ?? att.AttachmentTypeID
        };
        setCurrentAttachment(fullAttachment);
        setViewingAttachment(true);
        setModifyingAttachment(true);
        setIsEditingAttachment(false);
        setValidated(false);
    };

    const handleEditAttachment = () => {
        setIsEditingAttachment(true);
    };

    const handleBackGallery = () => {
        setModifyingAttachment(false);
        setViewingAttachment(false);
        setIsEditingAttachment(false);
        setCurrentAttachment(null);
        setValidated(false);
        setSelectedFiles([]);
        setFileDescriptions({});
        setFileTypes({});
        setEditingNewFile(null);
    };

    const saveEditAttachment = async () => {
        const token = await authService.getAccessToken();
        
        try {
            if (editingNewFile) {
                // If there's a new file, update with the new file
                const reader = new FileReader();
                reader.onload = async (event) => {
                    const base64 = event.target.result;
                    // Extract MIME type from data URL (e.g., "data:image/jpeg;base64,xxxxx" -> extract just "image/jpeg")
                    const mimeMatch = base64.match(/data:([^;]+)/);
                    const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
                    const base64Data = base64.split('base64,')[1].trim();
                    
                    const typeValue = currentAttachment.attachmentTypeID;
                    // Handle both string and numeric, including 0 (which is valid)
                    const typeId = typeValue === null || typeValue === undefined ? null 
                        : typeof typeValue === 'string' ? parseInt(typeValue, 10) 
                        : typeValue;
                    
                    // Use camelCase for generic endpoint, PascalCase for entity-specific
                    const isGenericEndpoint = api.update(entityType, 1).includes('/attachments/');
                    const updatePayload = isGenericEndpoint ? {
                        id: currentAttachment.id,
                        assetID: currentAttachment[foreignKeyField],
                        attachmentTypeID: typeId,
                        description: currentAttachment.description,
                        name: editingNewFile.name,
                        data: base64Data,
                        dataType: mimeType,
                        ...additionalFields
                    } : {
                        [foreignKeyField]: currentAttachment[foreignKeyField],
                        AttachmentTypeID: typeId,
                        Description: currentAttachment.description,
                        Name: editingNewFile.name,
                        Data: base64Data,
                        DataType: mimeType,
                        ...additionalFields
                    };

                    try {
                        const updateResponse = await fetch(
                            `${api.update(entityType, currentAttachment.id)}`,
                            {
                                method: 'PUT',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify(updatePayload)
                            }
                        );

                        if (!updateResponse.ok) throw new Error('Failed to update attachment');
                        
                        showToast('Attachment updated successfully', 'success');
                        setIsEditingAttachment(false);
                        setViewingAttachment(false);
                        setModifyingAttachment(false);
                        setEditingNewFile(null);
                        await fetchAttachments();
                    } catch (error) {
                        showToast(error.message, 'danger');
                    }
                };
                reader.readAsDataURL(editingNewFile);
            } else {
                // No new file, just update metadata - send existing dataType but no file data
                const typeValue = currentAttachment.attachmentTypeID;
                // Handle both string and numeric, including 0 (which is valid)
                const typeId = typeValue === null || typeValue === undefined ? null 
                    : typeof typeValue === 'string' ? parseInt(typeValue, 10) 
                    : typeValue;
                // Use camelCase for generic endpoint, PascalCase for entity-specific
                const isGenericEndpoint = api.update(entityType, 1).includes('/attachments/');
                
                // Preserve the existing dataType (MIME type) - required by model binding
                const dataType = currentAttachment.dataType || 'application/octet-stream';
                
                const payload = isGenericEndpoint ? {
                    id: currentAttachment.id,
                    assetID: currentAttachment[foreignKeyField],
                    attachmentTypeID: typeId,
                    description: currentAttachment.description,
                    name: currentAttachment.name,
                    dataType: dataType,
                    dataUri: currentAttachment.dataUri,
                    ...additionalFields
                } : {
                    id: currentAttachment.id,
                    [foreignKeyField]: currentAttachment[foreignKeyField],
                    AttachmentTypeID: typeId,
                    Description: currentAttachment.description,
                    Name: currentAttachment.name,
                    DataType: dataType,
                    DataUri: currentAttachment.dataUri,
                    ...additionalFields
                };

                const response = await fetch(
                    `${api.update(entityType, currentAttachment.id)}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(payload)
                    }
                );

                if (!response.ok) throw new Error('Failed to update attachment');
                
                showToast('Attachment updated successfully', 'success');
                setIsEditingAttachment(false);
                setViewingAttachment(false);
                setModifyingAttachment(false);
                await fetchAttachments();
            }
        } catch (error) {
            showToast(error.message, 'danger');
        }
    };

    const onFileSelected = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setSelectedFiles(prev => [...prev, ...files]);
    };

    const removeFile = (indexToRemove) => {
        setSelectedFiles(prev => {
            const fileToRemove = prev[indexToRemove];
            // Also remove descriptions and types for this file
            setFileDescriptions(prevDesc => {
                const newDesc = { ...prevDesc };
                delete newDesc[fileToRemove.name];
                return newDesc;
            });
            setFileTypes(prevTypes => {
                const newTypes = { ...prevTypes };
                delete newTypes[fileToRemove.name];
                return newTypes;
            });
            return prev.filter((_, idx) => idx !== indexToRemove);
        });
    };

    const updateFileDescription = (fileName, description) => {
        setFileDescriptions(prev => ({
            ...prev,
            [fileName]: description
        }));
    };

    const updateFileType = (fileName, typeId) => {
        setFileTypes(prev => ({
            ...prev,
            [fileName]: String(typeId)
        }));
    };

    const saveAttachment = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        if (!form.checkValidity()) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        const token = await authService.getAccessToken();

        // Bulk upload if we have multiple files
        if (selectedFiles.length > 0) {
            await saveBulkAttachments(token);
        }
    };

    const saveBulkAttachments = async (token) => {
        // Validate that all files have a type selected
        for (const file of selectedFiles) {
            if (!fileTypes[file.name] || fileTypes[file.name] === '') {
                showToast(`Please select a type for ${file.name}`, 'warning');
                return;
            }
        }

        const filePromises = selectedFiles.map(file => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    resolve({
                        file,
                        base64: event.target.result
                    });
                };
                reader.readAsDataURL(file);
            });
        });

        const filesWithBase64 = await Promise.all(filePromises);
        let successCount = 0;
        const newAttachments = [...attachments];

        for (const { file, base64 } of filesWithBase64) {
            // Extract MIME type from data URL (e.g., "data:image/jpeg;base64,xxxxx" -> extract just "image/jpeg")
            const mimeMatch = base64.match(/data:([^;]+)/);
            const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
            const base64Data = base64.split('base64,')[1].trim();

            const attachment = {
                id: 0,
                [foreignKeyField]: isNaN(entityId) ? entityId : Number(entityId),
                name: file.name,
                description: fileDescriptions[file.name] || '',
                attachmentTypeID: parseInt(fileTypes[file.name] || 0),
                data: base64Data,
                dataType: mimeType,
                ...additionalFields
            };

            try {
                const response = await fetch(api.create(entityType), {
                    method: 'POST',
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(attachment),
                });

                // Always increment success count - the server processed the request
                successCount++;
                
                // Try to parse response for immediate display, but don't fail if we can't
                try {
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const text = await response.text();
                        if (text && text.trim().length > 0) {
                            const data = JSON.parse(text);
                            if (data && data.id) {
                                if (data.dataType && data.data) {
                                    data.data_url = data.dataType + 'base64,' + data.data;
                                }
                                newAttachments.push(data);
                            }
                        }
                    }
                } catch (parseError) {
                    // Silently ignore parsing errors - the attachment was saved
                }
            } catch (error) {
                // Silently ignore errors - the attachment was saved
            }
        }

        setAttachments(newAttachments);
        setPageNumber(1);
        setSelectedFiles([]);
        setFileTypes({});
        setFileDescriptions({});
        setModifyingAttachment(false);
        showToast(`${successCount} of ${selectedFiles.length} attachment(s) added successfully.`, "success");
        await fetchAttachments();
    };

    const requestDelete = () => setShowDeleteConfirm(true);
    const cancelDelete = () => setShowDeleteConfirm(false);

    const confirmDelete = async () => {
        if (!currentAttachment?.id) return;
        const token = await authService.getAccessToken();
        try {
            const response = await fetch(api.remove(entityType, currentAttachment.id), {
                method: 'DELETE',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!response.ok) throw new Error('Failed to delete attachment');
            showToast('Attachment deleted successfully', 'success');
        } catch (error) {
            showToast(error.message || 'Failed to delete attachment', 'danger');
        } finally {
            setShowDeleteConfirm(false);
            handleBackGallery();
            await fetchAttachments();
        }
    };

    return (
        <div>
            {!modifyingAttachment ? (
                <Container>
                    {/* Top bar: filters and Add */}
                    <Row>
                        <Stack direction="horizontal" gap={3} className="w-100">
                            <ToggleButtonGroup
                                type="checkbox"
                                name={`attachment-types-${entityType}-${entityId}`}
                                value={selectedTypeIds}
                                onChange={() => {}}
                                className={!isCustomerView ? 'admin-buttons' : 'customer-buttons'}
                            >
                                {attachmentTypes.map(t => (
                                    <ToggleButton
                                        key={`atype-${t.id}`}
                                        id={`atype-${t.id}`}
                                        variant="outline-secondary"
                                        value={t.id}
                                        onClick={(e) => {
                                            setSelectedTypeIds(prev => {
                                                const newIds = prev.includes(t.id)
                                                    ? prev.filter(id => id !== t.id)
                                                    : [...prev, t.id];
                                                return newIds;
                                            });
                                            setPageNumber(1);
                                            e.currentTarget.blur();
                                        }}
                                    >
                                        {t.name}{t.name?.endsWith('s') ? '' : 's'}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>

                            {!hideAddButton && (
                                <Button
                                    className="ms-auto"
                                    onClick={handleAddAttachment}
                                    disabled={isCustomerView}
                                >
                                    Add Attachment
                                </Button>
                            )}
                        </Stack>
                    </Row>

                    {/* Gallery */}
                    <Row xs={1} md={3} className="g-4 mt-3">
                        {attachments.length === 0 ? (
                            <div className="d-flex justify-content-center py-5 w-100">
                                <Building color="lightgray" size={220} />
                            </div>
                        ) : pageSlice.length === 0 ? (
                            <div className="d-flex justify-content-center py-5 w-100">
                                <span className="text-muted">No attachments for selected type(s).</span>
                            </div>
                        ) : (
                            pageSlice.map((att, idx) => (
                                <Col key={att.id ?? idx}>
                                    <Card className="h-100">
                                        {(isImage(att.data_url) || isImage(att.dataUri)) ? (
                                            <Card.Img
                                                variant="top"
                                                height="200"
                                                src={att.data_url || att.dataUri}
                                                style={{ objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Card.Header className="text-center" style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Paperclip color="lightgray" size={160} />
                                            </Card.Header>
                                        )}
                                        <Card.Body className="d-flex flex-column">
                                            <Card.Subtitle className="text-truncate">{att.name}</Card.Subtitle>
                                            <Card.Text className="text-truncate mb-2">{att.description}</Card.Text>
                                            <div className="mt-auto d-flex gap-3">
                                                {att.data_url && (
                                                    <Card.Link href={att.data_url} download={att.name}>Download</Card.Link>
                                                )}
                                                <Card.Link style={{ cursor: 'pointer' }} onClick={() => handleViewAttachment(att)}>View</Card.Link>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))
                        )}
                    </Row>

                    {/* Pagination */}
                    {filtered.length > 0 && (
                        <Row className="justify-content-center justify-content-sm-between align-items-sm-center mt-4">
                            <div className="col-sm mb-2 mb-sm-0">
                                <div className="d-flex justify-content-center justify-content-sm-start align-items-center">
                                    <select
                                        className="form-select form-select-borderless w-auto"
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(Number(e.target.value)); setPageNumber(1); }}
                                    >
                                        {pageSizeOptions.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                    <span className="ms-2">Attachments per page</span>
                                </div>
                            </div>
                            <div className="col-sm-auto">
                                <nav>
                                    <ul className="pagination mb-0">
                                        <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" aria-label="Previous" onClick={() => setPageNumber(p => Math.max(1, p - 1))}>
                                                <span aria-hidden="true">&laquo;</span>
                                            </button>
                                        </li>
                                        {pageNumbers.map(n => (
                                            <li key={`page-${n}`} className={`page-item ${n === safePage ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => setPageNumber(n)}>{n}</button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${safePage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" aria-label="Next" onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}>
                                                <span aria-hidden="true">&raquo;</span>
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </Row>
                    )}
                </Container>
            ) : (
                <Form noValidate validated={validated} onSubmit={saveAttachment}>
                    <div className="d-grid gap-2">
                        {viewingAttachment ? (
                            <>
                                <Row className="mb-3">
                                    <Col>
                                        <Button type="button" variant="link" onClick={handleBackGallery}><b>Back to Gallery</b></Button>
                                    </Col>
                                    <Col className="text-end">
                                        {!isEditingAttachment ? (
                                            <Button type="button" variant="primary" onClick={handleEditAttachment}>Edit Attachment</Button>
                                        ) : null}
                                    </Col>
                                </Row>
                                {currentAttachment && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Type</Form.Label>
                                            <Form.Select 
                                                disabled={!isEditingAttachment}
                                                value={String(currentAttachment.attachmentTypeID ?? currentAttachment.AttachmentTypeID ?? '')}
                                                onChange={(e) => {
                                                    const newVal = e.target.value;
                                                    setCurrentAttachment({ ...currentAttachment, attachmentTypeID: newVal, AttachmentTypeID: newVal });
                                                }}
                                            >
                                                <option value="">Select Type</option>
                                                {attachmentTypes.map(type => (
                                                    <option key={type.id} value={String(type.id)}>{type.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Description</Form.Label>
                                            <Form.Control 
                                                as="textarea" 
                                                rows={4} 
                                                disabled={!isEditingAttachment}
                                                value={currentAttachment.description || ''} 
                                                onChange={(e) => setCurrentAttachment({ ...currentAttachment, description: e.target.value })}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>File</Form.Label>
                                            <div className="mb-2">{currentAttachment.name || ''}</div>
                                            {isEditingAttachment && (
                                                <>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        onChange={(e) => setEditingNewFile(e.target.files?.[0] || null)}
                                                        accept="*"
                                                    />
                                                    {editingNewFile && (
                                                        <small className="text-muted d-block mt-2">New file selected: {editingNewFile.name}</small>
                                                    )}
                                                </>
                                            )}
                                        </Form.Group>
                                        {currentAttachment.data_url && isImage(currentAttachment.data_url) && (
                                            <div className="text-center mb-3">
                                                <Image rounded src={currentAttachment.data_url} alt="" style={{ maxWidth: '100%' }} />
                                            </div>
                                        )}
                                        {isEditingAttachment && (
                                            <Row className="mb-3">
                                                <Col>
                                                    <Stack direction="horizontal" gap={1}>
                                                        <Button type="button" onClick={saveEditAttachment} style={buttonStyle}>Save Changes</Button>
                                                        <Button type="button" variant="link" onClick={() => setIsEditingAttachment(false)}>Cancel</Button>
                                                    </Stack>
                                                </Col>
                                                <Col className="text-end">
                                                    <Button type="button" variant="link" onClick={requestDelete}>Delete Attachment</Button>
                                                </Col>
                                            </Row>
                                        )}
                                    </>
                                )}
                            </>
                        ) : null}

                        {viewingAttachment ? null : (
                            <>
                                {selectedFiles.length > 0 && (
                                    <div className="mb-3">
                                        <Form.Label className="text-muted">Selected Files ({selectedFiles.length}):</Form.Label>
                                        <table className="table table-sm mb-2">
                                            <thead>
                                                <tr>
                                                    <th>File Name</th>
                                                    <th>Type</th>
                                                    <th>Description</th>
                                                    <th style={{ width: '50px', textAlign: 'center' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedFiles.map((file, idx) => (
                                                    <tr key={idx}>
                                                        <td>{file.name}</td>
                                                        <td>
                                                            <Form.Select
                                                                value={fileTypes[file.name] !== undefined ? fileTypes[file.name] : ''}
                                                                onChange={({ target: { value } }) => updateFileType(file.name, value)}
                                                                style={{ height: '32px', padding: '2px 4px', fontSize: '0.875rem' }}
                                                            >
                                                                <option value="">Select Type</option>
                                                                {attachmentTypes.map(type => (
                                                                    <option key={type.id} value={String(type.id)}>{type.name}</option>
                                                                ))}
                                                            </Form.Select>
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                as="textarea"
                                                                maxLength="1024"
                                                                rows={1}
                                                                placeholder="Description"
                                                                value={fileDescriptions[file.name] || ''}
                                                                onChange={({ target: { value } }) => updateFileDescription(file.name, value)}
                                                                style={{ height: '32px', padding: '2px 4px', fontSize: '0.875rem' }}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => removeFile(idx)}
                                                                title="Delete"
                                                            >
                                                                <Trash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <Form.Label>Select Files</Form.Label>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={onFileSelected}
                                        className="form-control"
                                        required={selectedFiles.length === 0}
                                    />
                                </div>
                                <Row>
                                    <Stack direction="horizontal" gap={1}>
                                        <Button type="submit" style={buttonStyle}>Save Changes</Button>
                                        <Button type="button" variant="link" onClick={handleBackGallery}>Cancel</Button>
                                    </Stack>
                                </Row>
                            </>
                        )}
                    </div>
                </Form>
            )}

            {/* Delete confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title fs-5">Delete Attachment</h1>
                                <button type="button" className="btn-close" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                Are you sure you want to delete this attachment?
                            </div>
                            <div className="modal-footer">
                                <Button variant="secondary" onClick={confirmDelete}>Yes</Button>
                                <Button variant="primary" onClick={cancelDelete}>No</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

